import { Fragment } from "react";
import type { DBEquipmentBookingLine, DBEquipmentItem } from "@/lib/db-types";
import { formatDayRange } from "@/lib/equipment/availability";
import { dayHeader, isWeekend, occupancyRow, type OccupancyCell, type OccupancyGroup } from "@/lib/equipment/occupancy";

interface OccupancyGridProps {
  groups: OccupancyGroup<DBEquipmentItem>[];
  days: string[];
  lines: DBEquipmentBookingLine[];
  today: string;
  onOpenBooking: (bookingId: string) => void;
  onBookFree: (itemId: string, day: string) => void;
}

const SERVICE_HATCH = { backgroundImage: "repeating-linear-gradient(45deg, hsl(var(--muted)) 0 4px, transparent 4px 8px)" };

const KIND_CLASS: Record<OccupancyCell["kind"], string> = {
  free: "hover:bg-muted/60",
  booked: "bg-primary text-primary-foreground font-medium",
  conflict: "bg-destructive text-destructive-foreground font-semibold",
  service: "",
};

const subtitle = (item: DBEquipmentItem) =>
  item.status === "in_service" ? "på service" : item.quantity > 1 ? `vi eier ${item.quantity}` : (item.serial_numbers[0] ?? "");

function cellLabel(item: DBEquipmentItem, cell: OccupancyCell): string {
  const day = formatDayRange(cell.day, cell.day);
  if (cell.kind === "free") return `Book ${item.name} ${day}`;
  if (cell.kind === "service") return `${item.name} er på service ${day}`;
  if (cell.kind === "conflict") return `${item.name} er dobbeltbooket ${day}, åpne bookingen`;
  return `${item.name} er booket ${day}, åpne bookingen`;
}

/** Belegg-rutenettet (mockup G): utstyr × dager, gruppert på avdeling. */
export function OccupancyGrid({ groups, days, lines, today, onOpenBooking, onBookFree }: OccupancyGridProps) {
  const columns = { gridTemplateColumns: `minmax(180px, 220px) repeat(${days.length}, minmax(36px, 1fr))` };
  const border = "border-b border-r border-border";

  return (
    <div className="overflow-x-auto">
      <div className="grid min-w-[760px] text-xs" style={columns} role="grid" aria-label="Belegg">
        <div className={border} />
        {days.map((d) => (
          <div
            key={d}
            className={`${border} py-2 text-center text-[11px] font-medium uppercase tracking-wider text-muted-foreground ${isWeekend(d) ? "bg-muted/40" : ""} ${d === today ? "text-foreground" : ""}`}
          >
            {dayHeader(d)}
          </div>
        ))}
        {groups.map((group) => (
          <Fragment key={group.departmentId ?? "none"}>
            <div className={`${border} col-span-full bg-muted px-2 py-2 font-display text-[13px] font-semibold`}>{group.name}</div>
            {group.items.map((item) => (
              <Fragment key={item.id}>
                <div className={`${border} px-2 py-1.5`}>
                  <div className="truncate text-[13px] font-medium" title={item.name}>{item.name}</div>
                  <div className="truncate text-muted-foreground">{subtitle(item)}</div>
                </div>
                {occupancyRow(item, lines, days).map((cell) => (
                  <button
                    key={cell.day}
                    type="button"
                    aria-label={cellLabel(item, cell)}
                    disabled={cell.kind === "service"}
                    onClick={() => (cell.bookingId ? onOpenBooking(cell.bookingId) : onBookFree(item.id, cell.day))}
                    className={`${border} min-h-9 truncate px-1 text-left ${KIND_CLASS[cell.kind]} ${cell.kind === "free" && isWeekend(cell.day) ? "bg-muted/40" : ""}`}
                    style={cell.kind === "service" ? SERVICE_HATCH : undefined}
                  >
                    {cell.label}
                  </button>
                ))}
              </Fragment>
            ))}
          </Fragment>
        ))}
      </div>
    </div>
  );
}
